// Pure helpers for the tradie's own weekly timesheet (a `self` sheet: the crew
// sheet shape with one row, the tradie's). Mirrors the app's rules in
// lib/views/profile/weekly_timesheet so web and app agree; the hour maths
// reuse timesheetHelpers (dayMinutes, dayError) rather than a second
// calculator.
import dayjs from 'dayjs'
import {
  dayError,
  dayMinutes,
  isLockedLeaveDay,
} from '@/utils/timesheetHelpers'

export const WEEKLY_STATUS = {
  draft: 'Draft',
  awaiting: 'Awaiting External Approval',
  approved: 'Approved',
  amended: 'Approved with Changes',
  adminApproved: 'Approved by Company Admin',
  rejected: 'Rejected - Amendment Required',
  notSent: 'Not sent',
  finalised: 'Finalised',
}

// One label for a full sheet (GET /my-week-sheets/{id}) or a history row
// (GET /my-week-sheets), same as the app's displayStatus / fromListRow. Only
// a list row turns an unsent draft whose week is over into "Not sent".
export function weeklyStatusLabel(sheet, now = dayjs()) {
  if (!sheet) return ''
  if (sheet.finalised_at) return WEEKLY_STATUS.finalised
  switch (sheet.status) {
    case 'submitted':
      return WEEKLY_STATUS.awaiting
    case 'approved': {
      if (sheet.admin_approval || sheet.approved_by_admin) {
        return WEEKLY_STATUS.adminApproved
      }
      const decision =
        sheet.decision ||
        (sheet.external_request && sheet.external_request.decision)
      return decision === 'amended'
        ? WEEKLY_STATUS.amended
        : WEEKLY_STATUS.approved
    }
    case 'rejected':
      return WEEKLY_STATUS.rejected
    default:
      if (
        !sheet.rows &&
        sheet.week_start &&
        !dayjs(sheet.week_start).add(7, 'day').isAfter(now, 'day')
      ) {
        return WEEKLY_STATUS.notSent
      }
      return WEEKLY_STATUS.draft
  }
}

export function weeklyStatusVariant(label) {
  switch (label) {
    case WEEKLY_STATUS.awaiting:
      return 'warning'
    case WEEKLY_STATUS.approved:
    case WEEKLY_STATUS.amended:
    case WEEKLY_STATUS.adminApproved:
    case WEEKLY_STATUS.finalised:
      return 'success'
    case WEEKLY_STATUS.rejected:
      return 'danger'
    default:
      return 'secondary'
  }
}

// A draft, one sent back, or one still awaiting approval. Approved is locked.
export function isEditableByOwner(sheet) {
  return !!sheet && ['draft', 'rejected', 'submitted'].includes(sheet.status)
}

// Saving a sheet awaiting approval returns it to draft and cancels its link.
export function editCancelsLink(sheet) {
  return !!sheet && sheet.status === 'submitted'
}

export function ownDays(sheet) {
  return (sheet && sheet.rows && sheet.rows[0] && sheet.rows[0].days) || []
}

export function weekTotalMinutes(days) {
  return (days || []).reduce(
    (sum, d) => sum + (d.leave_type ? 0 : dayMinutes(d)),
    0,
  )
}

// The first day error, labelled with its date, or null when every day is valid.
export function firstDayError(days) {
  for (const d of days || []) {
    const err = d.leave_type ? null : dayError(d)
    if (err) return `${dayjs(d.date).format('ddd D MMM')}: ${err}`
  }
  return null
}

function hasEnteredHours(day) {
  return !!(day.present || day.start || day.finish || day.leave_type)
}

// What a Standard Hours fill would do, without changing anything: days it
// would overwrite (they already hold hours or leave) and admin-locked leave
// days it must skip. `dayIndices` null means the whole week.
export function standardHoursImpact(days, dayIndices = null) {
  let conflicts = 0
  let skippedLeave = 0
  ;(days || []).forEach((day, i) => {
    if (dayIndices && !dayIndices.includes(i)) return
    if (isLockedLeaveDay(day)) skippedLeave++
    else if (hasEnteredHours(day)) conflicts++
  })
  return { conflicts, skippedLeave }
}

// One start/finish/break across the chosen days (or the whole week). Locked
// leave is left alone; the tradie's own leave goes back to Worked, as the
// API ignores times on a leave day.
export function fillStandardHours(
  days,
  { start, finish, breakMinutes },
  dayIndices = null,
) {
  return (days || []).map((day, i) => {
    if ((dayIndices && !dayIndices.includes(i)) || isLockedLeaveDay(day)) {
      return day
    }
    return {
      ...day,
      start,
      finish,
      break_minutes: Number(breakMinutes) || 0,
      present: true,
      leave_type: null,
    }
  })
}

// Edits one field on a day, keeping present/leave consistent the way the API
// reads them: times make it a worked day, leave clears the times.
export function withDayField(day, field, value) {
  const next = { ...day, [field]: value }
  if (field === 'leave_type') {
    next.leave_type = value || null
    if (next.leave_type) {
      next.start = null
      next.finish = null
      next.break_minutes = 0
      next.present = false
    }
  } else if (field === 'start' || field === 'finish') {
    next[field] = value || null
    next.present = !!(next.start || next.finish)
  } else if (field === 'break_minutes') {
    next.break_minutes = value === '' || value === null ? 0 : Number(value)
  }
  return next
}

// PUT /my-week-sheets/{id}. Only the tradie's own row; leave hours are a
// company admin's to set, so they are never sent. Plant goes only when it was
// touched this session: an absent key leaves it alone, [] clears it.
export function buildSaveBody({ workerId, days, comment, plantEntries }) {
  const body = {
    supervisor_comment: comment || null,
    rows: [
      {
        worker: { id: workerId },
        on_site: true,
        days: (days || []).map((d) => ({
          date: d.date,
          start: d.leave_type ? null : d.start || null,
          finish: d.leave_type ? null : d.finish || null,
          break_minutes: d.leave_type ? 0 : Number(d.break_minutes) || 0,
          present: d.leave_type ? false : !!(d.start || d.finish || d.present),
          leave_type: d.leave_type || null,
        })),
      },
    ],
  }
  if (plantEntries !== undefined) {
    body.plant_entries = plantEntries.map((e) => ({
      date: e.date,
      plant_equipment_id: e.plant_equipment_id,
      quantity: e.quantity,
    }))
  }
  return body
}

// The version kinds the API records. Approvals and rejections are request
// decisions, not versions, so they never appear in this list.
export function versionKindLabel(kind) {
  return (
    {
      original: 'Submitted',
      resubmission: 'Resubmitted',
      amended: 'Approved with changes',
      admin_amended: 'Amended by company admin',
    }[kind] || kind
  )
}
